import { nextTick } from 'vue';
import { ViteSSG } from 'vite-ssg';
import { createPinia } from 'pinia';
import App from './App.vue';
import { routes } from './router';
import './main.css';

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

// Scroll to a same-page anchor below the fixed production nav. Vue Router's
// `{ el }` position ignores the overlaying header, so the offset is computed
// explicitly: measured nav height plus the element's own scroll-margin-top
// (prototype story steps declare their own). Returns false after scrolling
// manually so the router applies no further position. Fragments are plain ids,
// never CSS selectors: the hash is decoded and resolved with getElementById so
// malformed fragments cannot throw; missing targets resolve to no scroll.
function scrollToHash(hash: string): false {
  if (typeof document === 'undefined') return false;
  let id = hash.startsWith('#') ? hash.slice(1) : hash;
  try {
    id = decodeURIComponent(id);
  } catch {
    return false;
  }
  if (!id) return false;
  const el = document.getElementById(id);
  if (el) {
    const nav = document.querySelector('.site-nav');
    const navHeight = nav ? nav.getBoundingClientRect().height : 72;
    const scrollMargin = Number.parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
    const top = Math.max(
      0,
      el.getBoundingClientRect().top + window.scrollY - navHeight - scrollMargin,
    );
    window.scrollTo({ top, left: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }
  return false;
}

export const createApp = ViteSSG(
  App,
  {
    routes,
    scrollBehavior(to, _from, savedPosition) {
      if (savedPosition) return savedPosition;
      const behavior = prefersReducedMotion() ? 'auto' : 'smooth';
      return new Promise((resolve) => {
        nextTick().then(() => {
          // In-page anchors (story navs, skip links): offset computed
          // explicitly in scrollToHash; plain anchor clicks on the same
          // route flow through here, no reload involved.
          if (to.hash) {
            resolve(scrollToHash(to.hash));
            return;
          }
          resolve({ top: 0, behavior });
        });
      });
    },
  },
  ({ app }) => {
    app.use(createPinia());
  },
);
